import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GanttSection, GanttSkeleton } from "./components/gantt-section";
import { BundlesTable, BundleRow } from "./components/bundles-table";
import { useBundlesData } from "./components/bundles-table/hooks/useBundlesData";
import { useWeldLines } from "./hooks/useWeldLines";
import { useSawLines } from "./hooks/useSawLines";
import { ATHENS_GRAY } from "@/shared/colors";

export const HomePage = () => {
	const [error, setError] = useState("");
	const [ganttContainerWidth, setGanttContainerWidth] = useState(0);
	const ganttPaperRef = useRef<HTMLDivElement>(null);

	const [searchParams, setSearchParams] = useSearchParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const { state } = useUTLSettingsContext();
	const weldsAssetId = Number(get(state, "settings.DefaultAreaId", 0));

	const initialShiftId = searchParams.get("shiftId");
	// biome-ignore lint/correctness/useExhaustiveDependencies: init only, on settings resolve
	useEffect(() => {
		if (!weldsAssetId) return;
		(async () => {
			await getShiftDataFromAPI(
				null,
				initialShiftId ? Number(initialShiftId) : 0,
				weldsAssetId,
			);
		})();
	}, [weldsAssetId]);

	const currentShiftId = shiftInfo?.CurrentShiftId ?? null;
	// biome-ignore lint/correctness/useExhaustiveDependencies: drive off shift changes only
	useEffect(() => {
		if (!currentShiftId) return;
		if (searchParams.get("shiftId") === String(currentShiftId)) return;
		const next = new URLSearchParams();
		next.set("shiftId", String(currentShiftId));
		setSearchParams(next, { replace: true });
	}, [currentShiftId]);

	const startDate = shiftInfo ? new Date(shiftInfo.CurrentStart) : null;
	const endDate = shiftInfo ? new Date(shiftInfo.CurrentEnd) : null;

	const handleError = useCallback((e: Error) => {
		setError(e.message);
	}, []);

	useEffect(() => {
		if (!ganttPaperRef.current) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setGanttContainerWidth(entry.contentRect.width);
			}
		});

		resizeObserver.observe(ganttPaperRef.current);
		return () => resizeObserver.disconnect();
	}, []);

	const { rows: bundleRows, isLoading: bundlesLoading } = useBundlesData({
		startDate,
		endDate,
		onError: handleError,
	});

	const { weldLines } = useWeldLines({ onError: handleError });
	const { sawLines } = useSawLines({ onError: handleError });

	// Weld lanes then the tracked saw, two-up (md:6)
	const productionLines = [...weldLines, ...sawLines];

	const bundlesByAsset = useMemo(() => {
		const map: Record<number, BundleRow[]> = {};
		for (const row of bundleRows) {
			(map[row.AssetId] ??= []).push(row);
		}
		return map;
	}, [bundleRows]);

	return (
		<>
			<HelmetDexteel title="Home" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Production Overview
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<ShiftDayNavigatorControl
							preventInit
							withPaper={false}
							assetId={weldsAssetId}
						/>
					</Paper>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper ref={ganttPaperRef} elevation={2} sx={{ padding: 2 }}>
						<Typography
							variant="h6"
							style={{ fontWeight: 600, marginBottom: 8 }}
						>
							Gantt
						</Typography>
						{startDate && endDate ? (
							<GanttSection
								startDate={startDate}
								endDate={endDate}
								viewMode="Bundles"
								containerWidth={ganttContainerWidth}
								onError={handleError}
								multiLine
							/>
						) : (
							<GanttSkeleton laneCount={6} rowHeight={120} />
						)}
					</Paper>
				</Grid2>

				{productionLines.map((line, index) => {
					const isLoneLast =
						index === productionLines.length - 1 &&
						productionLines.length % 2 === 1;
					return (
						<Grid2
							key={line.AssetId}
							size={{ md: isLoneLast ? 12 : 6, xs: 12 }}
						>
							<Paper elevation={2} sx={{ padding: 2, height: "100%" }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 1,
									}}
								>
									<Box
										sx={{
											width: 14,
											height: 14,
											borderRadius: "50%",
											backgroundColor: line.colors.main,
											border: "1px solid rgba(0, 0, 0, 0.2)",
											flexShrink: 0,
										}}
									/>
									<Typography variant="h6" style={{ fontWeight: 600 }}>
										{line.AssetName} Bundles
									</Typography>
								</Box>
								<BundlesTable
									rows={bundlesByAsset[line.AssetId] ?? []}
									loading={bundlesLoading}
								/>
							</Paper>
						</Grid2>
					);
				})}
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
